/**
 * Binary `.uv2` sidecar format (version 1) written by the lightmapper bake pipeline.
 *
 * Layout (little-endian):
 * - 0..3   magic "GUV2"
 * - 4..7   version uint32 (= 1)
 * - 8..11  vertexCount uint32
 * - 12..   float32 uv pairs (u0, v0, u1, v1, …)
 */

/** Bytes `GUV2` interpreted as a little-endian uint32 (`0x47 0x55 0x56 0x32`). */
const UV2_MAGIC = 0x32565547;
const UV2_VERSION = 1;
const UV2_HEADER_SIZE = 12;

export interface ParsedUv2Binary {
  vertexCount: number;
  uv: Float32Array;
}

export function parseUv2Binary(bytes: Uint8Array): ParsedUv2Binary {
  if (bytes.byteLength < UV2_HEADER_SIZE) {
    throw new Error(`UV2 sidecar too small: ${bytes.byteLength} bytes`);
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const magic = view.getUint32(0, true);
  if (magic !== UV2_MAGIC) {
    throw new Error('Invalid UV2 sidecar: unknown uv2 binary, please re-bake');
  }

  const version = view.getUint32(4, true);
  if (version !== UV2_VERSION) {
    throw new Error(`Unsupported UV2 sidecar version: ${version}, please re-bake`);
  }

  const vertexCount = view.getUint32(8, true);
  const expectedBytes = UV2_HEADER_SIZE + vertexCount * 8;
  if (bytes.byteLength !== expectedBytes) {
    throw new Error(
      `UV2 sidecar size mismatch: expected ${expectedBytes} bytes, got ${bytes.byteLength}, please re-bake`,
    );
  }

  const uv = new Float32Array(bytes.buffer, bytes.byteOffset + UV2_HEADER_SIZE, vertexCount * 2);
  return { vertexCount, uv };
}
